import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import PredictWidget from '../../components/caseStudies/worldCup/PredictWidget';
import KnockoutWidget from '../../components/caseStudies/worldCup/KnockoutWidget';
import StandingsWidget from '../../components/caseStudies/worldCup/StandingsWidget';
import SettleWidget from '../../components/caseStudies/worldCup/SettleWidget';
import { AVATARS, gameLogo } from '../../components/caseStudies/worldCup/assets';
import './WorldCupCaseStudy.css';

const K = 'caseStudies.worldCup';

// Only our own results are shown: the client's audience figures stay private.
// Finals night (FINAL_NUMBERS ledger): ~7x baseline load, under 2% of capacity.
const PEAK_UTILIZATION = 2; // percent, upper bound
// Avatars from the game's own set for the two players quoted (initials only).
const PLAYER_AVATARS = [42, 38];
const TECH = ['React', 'Node.js', 'PostgreSQL', 'Redis', 'Docker', 'Nginx', 'Cloudflare', 'WebSockets'];

const Pitchline = () => <div className="pitchline" role="presentation" />;

const WorldCupCaseStudyPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language) === 'he' ? 'he' : 'en';

  useEffect(() => {
    const previous = document.title;
    document.title = t(`${K}.metaTitle`);
    return () => { document.title = previous; };
  }, [t]);

  const scorebar = t(`${K}.hero.scorebar`, { returnObjects: true });
  const results = t(`${K}.numbers.results`, { returnObjects: true });
  const stories = t(`${K}.engineering.stories`, { returnObjects: true });
  const nodes = t(`${K}.engineering.nodes`, { returnObjects: true });
  const side = t(`${K}.engineering.side`, { returnObjects: true });
  const playerQuotes = t(`${K}.players.quotes`, { returnObjects: true });

  return (
    <div className="wc-cs" lang={lang}>
      <div className="wc-page">
        <RouterLink className="wc-back" to="/case-studies">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="10 3 5 8 10 13" />
          </svg>
          {t('caseStudies.back')}
        </RouterLink>

        <header className="hero">
          <span className="eyebrow">{t(`${K}.hero.eyebrow`)}</span>
          <h1><Trans i18nKey={`${K}.hero.title`} components={{ em: <em /> }} /></h1>
          <p className="sub">{t(`${K}.hero.sub`)}</p>
          <p className="sub">{t(`${K}.hero.sub2`)}</p>
          <div className="scorebar" role="group" aria-label={t(`${K}.hero.scorebarLabel`)}>
            {scorebar.map((item) => (
              <div key={item.k}>
                <span className="k">{item.k}</span>
                <span className="v">{item.v}</span>
              </div>
            ))}
          </div>
        </header>

        <Pitchline />

        <section>
          <span className="eyebrow">{t(`${K}.numbers.eyebrow`)}</span>
          <h2>{t(`${K}.numbers.title`)}</h2>
          <p className="lede">{t(`${K}.numbers.lede`)}</p>
          <div className="finals">
            <p className="finals-label">{t(`${K}.numbers.finals.label`)}</p>
            <div className="finals-figs">
              <div>
                <span className="fig" dir="ltr">×7</span>
                <span className="fig-lbl">{t(`${K}.numbers.finals.load`)}</span>
              </div>
              <div>
                <span className="fig" dir="ltr">&lt;{PEAK_UTILIZATION}%</span>
                <span className="fig-lbl">{t(`${K}.numbers.finals.util`)}</span>
              </div>
            </div>
            {/* The whole track is the server's capacity; the sliver is what finals night used. */}
            <div className="capbar" role="img" aria-label={t(`${K}.numbers.finals.barLabel`)}>
              <div className="capbar-fill" style={{ width: `${PEAK_UTILIZATION}%` }} />
            </div>
            <div className="capbar-scale" aria-hidden="true">
              <span dir="ltr">0%</span>
              <span>{t(`${K}.numbers.finals.scaleEnd`)}</span>
            </div>
          </div>
          <div className="results">
            {results.map((r) => (
              <div className="result" key={r.fig}>
                <span className="fig" dir="auto">{r.fig}</span>
                <span className="fig-lbl">{r.lbl}</span>
              </div>
            ))}
          </div>
        </section>

        <Pitchline />

        <div className="chapter">
          <span className="no">1</span>
          <div className="ch-tx">
            <span className="eyebrow">{t(`${K}.product.eyebrow`)}</span>
            <h2>{t(`${K}.product.title`)}</h2>
          </div>
        </div>

        <section>
          <div className="demo-grid">
            <PredictWidget lang={lang} />
            <KnockoutWidget lang={lang} />
            <StandingsWidget lang={lang} />
            <SettleWidget lang={lang} />
          </div>
        </section>

        <Pitchline />

        <div className="chapter">
          <span className="no">2</span>
          <div className="ch-tx">
            <span className="eyebrow">{t(`${K}.engineering.eyebrow`)}</span>
            <h2>{t(`${K}.engineering.title`)}</h2>
          </div>
        </div>

        <section>
          <h2 className="sub-h">{t(`${K}.engineering.storiesTitle`)}</h2>
          <div className="stories">
            {stories.map((story, i) => (
              <div className="story" key={story.title}>
                <h3>{story.title}</h3>
                <p>
                  <Trans i18nKey={`${K}.engineering.stories.${i}.body`} components={{ stat: <span className="stat" /> }} />
                </p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="sub-h">{t(`${K}.engineering.archTitle`)}</h2>
          <p className="lede">{t(`${K}.engineering.archLede`)}</p>
          <div className="arch-card">
            <div className="arch-flow" role="img" aria-label={t(`${K}.engineering.archLabel`)}>
              {nodes.map((node, i) => (
                <React.Fragment key={node.name}>
                  {i > 0 && <div className="arr">{t(`${K}.engineering.arrow`)}</div>}
                  <div className={`node${i === 3 ? ' hl' : ''}`}>{node.name}<small>{node.small}</small></div>
                </React.Fragment>
              ))}
            </div>
            <div className="arch-side">
              {side.map((chip) => (
                <span className="side-chip" key={chip.b}><b>{chip.b}</b> · {chip.text}</span>
              ))}
            </div>
          </div>
        </section>

        <Pitchline />

        <section>
          <span className="eyebrow">{t(`${K}.quote.eyebrow`)}</span>
          <blockquote className="quote">
            <p>{t(`${K}.quote.text`)}</p>
            <footer>
              <img src={gameLogo} alt="" />
              <span>{t(`${K}.quote.footer`)}</span>
            </footer>
          </blockquote>
        </section>

        <section>
          <span className="eyebrow">{t(`${K}.players.eyebrow`)}</span>
          <div className="voices">
            {playerQuotes.map((q, i) => (
              <figure className="voice" key={q.by}>
                <blockquote><p>{q.text}</p></blockquote>
                <figcaption>
                  <img src={AVATARS[PLAYER_AVATARS[i]]} alt="" />
                  <span>{q.by}</span>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="voices-note">{t(`${K}.players.note`)}</p>
        </section>

        <section>
          <span className="eyebrow">{t(`${K}.tech.eyebrow`)}</span>
          <div className="stack">
            {TECH.map((name) => <span className="chip" key={name}>{name}</span>)}
          </div>
          <p className="credits">
            <Trans i18nKey={`${K}.tech.client`} components={{ b: <b /> }} /><br />
            <Trans i18nKey={`${K}.tech.team`} components={{ b: <b /> }} /><br />
            {t(`${K}.tech.period`)}
          </p>
        </section>

        <div className="wc-cta">
          <p>{t('caseStudies.cta.title')}</p>
          <RouterLink to="/contact">{t('caseStudies.cta.button')}</RouterLink>
        </div>
      </div>
    </div>
  );
};

export default WorldCupCaseStudyPage;
